/**
 * HashtagSuggestionsList
 *
 * Scrollable list of hashtag suggestions shown while editing a #hashtag.
 * Matches Flutter's _hashtagsList from set_content_description_screen.dart.
 */

import React, { useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { VirtualList } from '@/presentation/components/common/virtualList/VirtualList';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { qaAttr } from '@/presentation/utils/qaAttr';

interface HashtagSuggestionsListProps {
  suggestions: ContentTagEntity[];
  isFetching: boolean;
  onSelect: (tag: ContentTagEntity) => void;
  onLoadMore: () => void;
}

export const HashtagSuggestionsList: React.FC<HashtagSuggestionsListProps> = ({
  suggestions,
  isFetching,
  onSelect,
  onLoadMore,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const getUsesLabel = (tag: ContentTagEntity): string | null => {
    if (tag.localUses > 0) return null;
    if (tag.uses === 0) return l10n.publish.hashtagAdd;
    return l10n.publish.hashtagUses.replace('{count}', String(tag.uses));
  };

  const getItemKey = useCallback((tag: ContentTagEntity) => tag.name, []);

  const renderItem = useCallback((tag: ContentTagEntity) => {
    const usesLabel = getUsesLabel(tag);

    return (
      <button
        {...qaAttr({ id: `hashtag-suggestion-${tag.name}` })}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onSelect(tag)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: responsive.spacing.lg,
          backgroundColor: theme.transparent,
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.bold,
          }}
        >
          #{tag.name}
        </span>
        {usesLabel !== null ? (
          <span
            style={{
              color: theme.text.secondary,
              fontSize: responsive.font.base,
              fontWeight: responsive.fontWeight.bold,
            }}
          >
            {usesLabel}
          </span>
        ) : (
          <AssetIcon
            path="/assets/vectors/star_filled.svg"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
        )}
      </button>
    );
  }, [onSelect, theme, responsive, l10n]);

  return (
    <div>
      <VirtualList
        items={suggestions}
        renderItem={renderItem}
        getItemKey={getItemKey}
        defaultItemHeight={48}
        gap={0}
        onReachedEnd={onLoadMore}
      />

      {isFetching && (
        <div style={{ padding: responsive.spacing.sm }}>
          <ExpauseLoadingIndicator />
        </div>
      )}
    </div>
  );
};
